import { useCallback, useRef, useState } from "react";
import { pxToSnappedPx, pxToTime } from "../utils/time-positioning";
import { TOTAL_GRID_HEIGHT_PX } from "../utils/time-constants";

const DRAG_THRESHOLD_PX = 5;

export interface SlotCreationState {
  readonly date: string;
  readonly startPx: number;
  readonly endPx: number;
}

export interface SlotCreationResult {
  readonly date: string;
  readonly startTime: string;
  readonly endTime: string;
}

interface UseSlotCreationReturn {
  readonly slotCreation: SlotCreationState | null;
  readonly startSlotCreation: (date: string, mouseY: number) => void;
  readonly updateSlotCreation: (mouseY: number) => void;
  readonly commitSlotCreation: () => SlotCreationResult | null;
  readonly cancelSlotCreation: () => void;
}

export function useSlotCreation(): UseSlotCreationReturn {
  const [slotCreation, setSlotCreation] = useState<SlotCreationState | null>(
    null,
  );
  const stateRef = useRef<SlotCreationState | null>(null);

  const startSlotCreation = useCallback((date: string, mouseY: number, freeSnap = false) => {
    const snappedY = clampPx(pxToSnappedPx(mouseY, freeSnap));
    const state: SlotCreationState = {
      date,
      startPx: snappedY,
      endPx: snappedY,
    };
    stateRef.current = state;
    setSlotCreation(state);
  }, []);

  const updateSlotCreation = useCallback((mouseY: number, freeSnap = false) => {
    const current = stateRef.current;
    if (!current) return;
    const snappedY = clampPx(pxToSnappedPx(mouseY, freeSnap));
    const next: SlotCreationState = { ...current, endPx: snappedY };
    stateRef.current = next;
    setSlotCreation(next);
  }, []);

  const commitSlotCreation = useCallback((): SlotCreationResult | null => {
    const current = stateRef.current;
    if (!current) return null;

    const { date, startPx, endPx } = current;
    const distance = Math.abs(endPx - startPx);

    if (distance < DRAG_THRESHOLD_PX) {
      stateRef.current = null;
      setSlotCreation(null);
      return null;
    }

    const topPx = Math.min(startPx, endPx);
    const bottomPx = Math.max(startPx, endPx);
    const startTime = pxToTime(topPx, true);
    const endTime = pxToTime(bottomPx, true);

    if (startTime === endTime) {
      stateRef.current = null;
      setSlotCreation(null);
      return null;
    }

    // Freeze preview at final position (normalized top→bottom)
    const frozen: SlotCreationState = { date, startPx: topPx, endPx: bottomPx };
    stateRef.current = frozen;
    setSlotCreation(frozen);

    return { date, startTime, endTime };
  }, []);

  const cancelSlotCreation = useCallback(() => {
    stateRef.current = null;
    setSlotCreation(null);
  }, []);

  return {
    slotCreation,
    startSlotCreation,
    updateSlotCreation,
    commitSlotCreation,
    cancelSlotCreation,
  };
}

function clampPx(px: number): number {
  return Math.max(0, Math.min(px, TOTAL_GRID_HEIGHT_PX));
}
